import { useEffect, useState } from "react";
import { apiGet } from "../../../services/api";
import { subscribeCareflowSync } from "../../../services/careflowSync";

export interface FinanceEventRow {
  debit?: number;
  credit?: number;
  source?: string;
  event_date?: string;
  date?: string; // YYYY-MM-DD (normalized for filtering)
  [key: string]: unknown;
}

export interface CashflowResult {
  inflow: number;
  outflow: number;
  net: number;
}

export interface PnLBySource {
  [source: string]: { revenue: number; expense: number };
}

export function useFinanceLedger(date?: string): {
  events: FinanceEventRow[];
  loading: boolean;
  error: unknown;
  refresh: () => Promise<void>;
} {
  const [events, setEvents] = useState<FinanceEventRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = date
        ? `/api/finance/events?start_date=${date}&end_date=${date}`
        : "/api/finance/events";
      const res = await apiGet(url);
      const list = Array.isArray(res) ? (res as FinanceEventRow[]) : [];
      setEvents(
        list.map((row) => {
          const eventDateRaw = String(row.event_date || row.date || "");
          // Extract YYYY-MM-DD using regex to be robust against "T" vs space or time inclusions
          const dateMatch = eventDateRaw.match(/^(\d{4}-\d{2}-\d{2})/);
          const normalizedDate = dateMatch ? dateMatch[1] : (eventDateRaw ? eventDateRaw.slice(0, 10) : "");
          return {
            ...row,
            date: normalizedDate || row.date,
            debit: Number(row.debit ?? 0),
            credit: Number(row.credit ?? 0),
          };
        })
      );
    } catch (e) {
      console.warn("Finance events endpoint not available yet", e);
      setError(e);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, [date]);

  useEffect(() => {
    let timer: number | null = null;
    const unsubscribe = subscribeCareflowSync(() => {
      if (timer != null) window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void refresh();
      }, 250);
    });
    return () => {
      if (timer != null) window.clearTimeout(timer);
      unsubscribe();
    };
  }, [date]);

  return { events, loading, error, refresh };
}

export function computeCashflow(events: FinanceEventRow[]): CashflowResult {
  return events.reduce<CashflowResult>(
    (acc, e) => {
      acc.inflow += Number(e.debit ?? 0);
      acc.outflow += Number(e.credit ?? 0);
      acc.net = acc.inflow - acc.outflow;
      return acc;
    },
    { inflow: 0, outflow: 0, net: 0 }
  );
}

/**
 * Sources that are purely EXPENDITURE (clinic pays out money).
 * These should never contribute to revenue in the P&L.
 */
const EXPENSE_ONLY_SOURCES = new Set([
  "CONSULTANT_PAYMENT",
  "CONSULTANT_FEE",
  "LAB_PAYMENT",
  "LAB_INTERNAL",
  "SUPPLIER_PAYMENT",
]);

/**
 * Sources that are purely REVENUE (clinic receives money from patients).
 * These should never contribute to expense in the P&L.
 */
const REVENUE_ONLY_SOURCES = new Set([
  "CLINICAL_PAYMENT",
  "PATIENT_PAYMENT",
  "PHARMACY_SALE",
  "PHARMACY",
]);

export function computePnL(events: FinanceEventRow[]): PnLBySource {
  const map: PnLBySource = {};

  events.forEach(e => {
    const source = (e.source || "UNKNOWN") as string;
    if (!map[source]) {
      map[source] = { revenue: 0, expense: 0 };
    }

    if (EXPENSE_ONLY_SOURCES.has(source)) {
      // Only count the outflow (credit = money clinic paid out)
      map[source].expense += Number(e.credit || 0) + Number(e.debit || 0);
    } else if (REVENUE_ONLY_SOURCES.has(source)) {
      // Only count the inflow (debit = money clinic received)
      map[source].revenue += Number(e.debit || 0) + Number(e.credit || 0);
    } else {
      // General / unknown: use standard debit=revenue, credit=expense
      map[source].revenue += Number(e.debit || 0);
      map[source].expense += Number(e.credit || 0);
    }
  });

  return map;
}
